/**
 * Loyiha bo‘ylab ogohlantirishlar — SaaS modali (brauzer alert o‘rniga).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from './LanguageContext';
import './AppNoticeContext.css';

export type AppNoticeVariant = 'info' | 'warning' | 'error';

export type AppNoticePayload = {
  title?: string;
  message: string;
  variant?: AppNoticeVariant;
  confirmLabel?: string;
};

type AppNoticeContextValue = {
  showNotice: (p: AppNoticePayload) => void;
};

const AppNoticeContext = createContext<AppNoticeContextValue | null>(null);

let dispatchRef: ((p: AppNoticePayload) => void) | null = null;

/** Provider tashqarisidan (masalan, servislar) chaqirish */
export function showAppNotice(payload: AppNoticePayload): void {
  if (dispatchRef) {
    dispatchRef(payload);
    return;
  }
  // eslint-disable-next-line no-console
  console.warn('[showAppNotice]', payload.message);
}

function NoticeIcon({ variant }: { variant: AppNoticeVariant }) {
  if (variant === 'error') {
    return (
      <svg className="app-notice-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
        <path d="M12 8v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        <circle cx="12" cy="16.5" r="1" fill="currentColor" />
      </svg>
    );
  }
  if (variant === 'warning') {
    return (
      <svg className="app-notice-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 3.5 2.5 20h19L12 3.5z"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinejoin="round"
        />
        <path d="M12 10v4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        <circle cx="12" cy="17.5" r="1" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg className="app-notice-icon-svg" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 11v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="8" r="1" fill="currentColor" />
    </svg>
  );
}

export function AppNoticeProvider({ children }: { children: React.ReactNode }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<AppNoticePayload | null>(null);

  const showNotice = useCallback((p: AppNoticePayload) => {
    setPayload(p);
    setOpen(true);
  }, []);

  useEffect(() => {
    dispatchRef = showNotice;
    return () => {
      dispatchRef = null;
    };
  }, [showNotice]);

  const close = useCallback(() => {
    setOpen(false);
    setPayload(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const value = useMemo(() => ({ showNotice }), [showNotice]);

  const variant = payload?.variant ?? 'info';
  const title =
    payload?.title?.trim() ||
    (variant === 'error'
      ? t('appNotice_title_error')
      : variant === 'warning'
        ? t('appNotice_title_warning')
        : t('appNotice_title_default'));

  const confirmLabel = payload?.confirmLabel?.trim() || t('appNotice_confirm') || t('common_ok') || 'OK';

  return (
    <AppNoticeContext.Provider value={value}>
      {children}
      {open &&
        payload &&
        createPortal(
          <div className="app-notice-overlay" role="presentation" onClick={close}>
            <div
              className={`app-notice-modal app-notice-modal--${variant}`}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="app-notice-title"
              aria-describedby="app-notice-body"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="app-notice-header">
                <div className="app-notice-icon-wrap" aria-hidden>
                  <NoticeIcon variant={variant} />
                </div>
                <div className="app-notice-header-text">
                  <p id="app-notice-title" className="app-notice-title">
                    {title}
                  </p>
                  <p id="app-notice-body" className="app-notice-body">
                    {payload.message}
                  </p>
                </div>
              </div>
              <div className="app-notice-actions">
                <button type="button" className="app-notice-btn" onClick={close} autoFocus>
                  {confirmLabel}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </AppNoticeContext.Provider>
  );
}

export function useAppNotice(): AppNoticeContextValue {
  const ctx = useContext(AppNoticeContext);
  if (!ctx) {
    return {
      showNotice: (p) => showAppNotice(p),
    };
  }
  return ctx;
}
